import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Trash2, FileText, Image as ImageIcon, Loader2, Eye } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic file uploader.
 * Props:
 *  - endpoint: e.g. `/api/tickets/{id}/arquivo`
 *  - accept: e.g. "image/*,application/pdf"
 *  - maxSizeMB: e.g. 20
 *  - currentUrl: string | null (URL already saved on parent)
 *  - onChange: (url) => void called after upload/delete
 *  - testId: prefix for data-testid
 *  - label: button label
 */
export default function FileUpload({
  endpoint,
  accept = "image/*,application/pdf",
  maxSizeMB = 20,
  currentUrl = null,
  onChange,
  testId = "file-upload",
  label = "Enviar arquivo",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);

  const token = localStorage.getItem("lab_token") || "";

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Arquivo excede ${maxSizeMB}MB`);
      return;
    }
    setBusy(true);
    setProgress(0);
    setPreview(URL.createObjectURL(file));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${BACKEND_URL}${endpoint}`, form, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Arquivo enviado");
      onChange?.(data.url, data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    // For full delete we'd need the file_id; here we just clear url
    onChange?.(null, null);
    setPreview(null);
    toast.info("Arquivo removido (do registro)");
  };

  const viewUrl = currentUrl
    ? `${BACKEND_URL}${currentUrl}${currentUrl.includes("?") ? "&" : "?"}auth=${token}`
    : null;

  const isImage = currentUrl && /\.(png|jpe?g|webp|gif)$/i.test(currentUrl);

  return (
    <div className="space-y-2" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleSelect}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {currentUrl ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-slate-50">
          {isImage ? (
            <img src={viewUrl} alt="preview" className="h-12 w-12 object-cover rounded" />
          ) : (
            <FileText size={20} style={{ color: "#1B3A6B" }} />
          )}
          <div className="flex-1 text-xs truncate">{currentUrl.split("/").pop()}</div>
          <a
            href={viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800"
            data-testid={`${testId}-view`}
          >
            <Eye size={12} /> Ver
          </a>
          <button
            type="button"
            onClick={handleDelete}
            className="text-xs flex items-center gap-1 text-red-500 hover:text-red-700"
            data-testid={`${testId}-delete`}
          >
            <Trash2 size={12} /> Remover
          </button>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testId}-btn`}
            className="w-full"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
            {busy ? `Enviando... ${progress}%` : label}
          </Button>
          {busy && <Progress value={progress} className="h-1 mt-1" />}
        </div>
      )}
    </div>
  );
}
